import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderOpen, Plus, Bot, CheckCircle2 } from 'lucide-react';
import { listProjects } from '../../services/projects';
import { DEFAULT_BRANCH } from '../../utils/projectPath';
import { getUiView } from '../../hooks/useUiView';
import CreateProjectModal from '../projects/CreateProjectModal';
import AppRequestDialog from '../AppRequestDialog';
import { NEW_PROJECT_KINDS } from '../../pages/Projects';
import { isPortal } from '../../utils/portal';
import { useAuth } from '@/contexts/AuthContext';

// Landing guard for project-less paths (e.g. "/"). The URL is the single source
// of truth for the current project, so when it carries no project we resolve the
// FIRST available project and redirect into it: /p/<firstSlug>/b/main/dashboard.
//
// When the user has NO projects (a brand-new org's first login), this screen is
// the only reachable surface — every app route, including /projects with its
// create wizard, needs a project in the URL. So it must offer a REAL create
// flow, not just tell the user to create a project: the shared
// CreateProjectModal is rendered right here, and a successful create navigates
// straight into the new project. Creation is org_admin+ on the backend; anyone
// else gets the 403 message inline in the dialog.
export default function RedirectToFirstProject() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Role is known client-side (/auth/me carries org memberships) — render
  // only what THIS user can actually do instead of hedging in the copy.
  const isAdmin = (user?.orgs || []).some((o) => o.role === 'org_admin' || o.role === 'platform_admin');
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showAssistant, setShowAssistant] = useState(false);
  const [assistantFiled, setAssistantFiled] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const projects = await listProjects();
        if (!alive) return;
        const firstRow = projects && projects[0];
        const first = firstRow && firstRow.slug;
        if (first) {
          // Portal deployments have no Dashboard page — Apps is home there.
          // Standard mode hides Dashboard (a data-steward surface) from its
          // nav, so it lands on Apps too; Advanced keeps Dashboard.
          //
          // A SHARE-ONLY row (an app_user, or anyone whose only grant is on a
          // single app/agent) has no Dashboard either: every project-scoped
          // page 403s for them, and Apps is the only surface they hold. Sending
          // them to 'dashboard' worked only because Layout then bounced them
          // here — one visible extra hop through a page they cannot open.
          // Reading the flag the API already returns keeps the landing honest.
          const shareOnly = !!firstRow.shared_surface_only;
          const home = shareOnly || isPortal() || getUiView() === 'simple' ? 'apps' : 'dashboard';
          navigate(`/p/${encodeURIComponent(first)}/b/${DEFAULT_BRANCH}/${home}`, { replace: true });
        } else {
          setError('no-projects');
        }
      } catch (e) {
        if (!alive) return;
        setError(e?.message || 'Failed to load projects');
      }
    })();
    return () => { alive = false; };
  }, [navigate]);

  if (error === 'no-projects') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', gap: 12, color: 'var(--page-text, #334155)', padding: 24, textAlign: 'center' }}>
        <FolderOpen size={44} style={{ color: 'var(--page-muted, #94a3b8)' }} />
        <div style={{ fontWeight: 600, fontSize: 18 }}>No projects yet</div>
        <div style={{ fontSize: 14, color: 'var(--page-muted, #64748b)', maxWidth: 420 }}>
          A project bundles your data sources, mappings, processing rules, and pipeline.
          Create your organization's first project to get started.
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button
            type="button"
            data-testid="first-run-assistant"
            onClick={() => setShowAssistant(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 18px', borderRadius: 6, border: 0, cursor: 'pointer', background: 'var(--nav-active, #3b82f6)', color: '#fff', fontSize: 14, fontWeight: 500 }}
          >
            <Bot size={16} /> Project assistant
          </button>
          {isAdmin && (
            <button
              type="button"
              data-testid="first-run-create-project"
              onClick={() => setShowCreate(true)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 18px', borderRadius: 6, border: '1px solid var(--card-border, #e2e8f0)', cursor: 'pointer', background: 'transparent', color: 'var(--page-text, #334155)', fontSize: 14, fontWeight: 500 }}
            >
              <Plus size={16} /> Blank project
            </button>
          )}
        </div>
        {assistantFiled && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--page-text, #334155)', background: 'var(--card-bg, #fff)', border: '1px solid var(--card-border, #e2e8f0)', borderRadius: 8, padding: '10px 14px' }}>
            <CheckCircle2 size={15} style={{ color: '#16a34a' }} />
            Project request filed — an agent is building it. The project appears here when it's ready.
          </div>
        )}
        {showAssistant && (
          <AppRequestDialog
            kinds={NEW_PROJECT_KINDS.filter((k) => k.kind === 'new_project')}
            title="Project assistant"
            onClose={() => setShowAssistant(false)}
            onCreated={() => { setShowAssistant(false); setAssistantFiled(true); }}
          />
        )}
        <div style={{ fontSize: 12, color: 'var(--page-muted, #94a3b8)' }}>
          {isAdmin
            ? 'Describe the project and the assistant builds it end to end — or start from a blank one.'
            : 'Describe the project and the assistant builds it for you.'}
        </div>
        {showCreate && (
          <CreateProjectModal
            onClose={() => setShowCreate(false)}
            onSuccess={(slug) => {
              // Land directly in the new project; the URL is the source of truth.
              navigate(`/p/${encodeURIComponent(slug)}/b/${DEFAULT_BRANCH}/dashboard`, { replace: true });
            }}
          />
        )}
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#b91c1c' }}>
        {String(error)}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: 'var(--page-muted, #64748b)' }}>
      Loading…
    </div>
  );
}
